import { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useCustomers, Customer } from "@/contexts/CustomerContext";
import { useAuth, hasPermission } from "@/contexts/AuthContext";
import { Search, Plus, Trash2, Phone, Mail, Users, Flame, UserPlus, PauseCircle, CheckCircle, ArrowRight, TrendingUp } from "lucide-react";
import "./Customers.css";

const STATUSES = ["all", "hot_lead", "active", "new", "stalled", "closed"] as const;

const STATUS_CONFIG: Record<string, { label: string; color: string; bg: string; gradient: string }> = {
  hot_lead: { label: "Hot Lead", color: "#F97316", bg: "#FFF7ED", gradient: "linear-gradient(135deg, #F97316, #FB923C)" },
  active: { label: "Active", color: "#10B981", bg: "#ECFDF5", gradient: "linear-gradient(135deg, #10B981, #34D399)" },
  new: { label: "New", color: "#1B6EF3", bg: "#E8F0FE", gradient: "linear-gradient(135deg, #1B6EF3, #3B82F6)" },
  stalled: { label: "Stalled", color: "#EAB308", bg: "#FEFCE8", gradient: "linear-gradient(135deg, #EAB308, #FDE047)" },
  closed: { label: "Closed", color: "#6B7280", bg: "#F3F4F6", gradient: "linear-gradient(135deg, #6B7280, #9CA3AF)" },
};

function timeAgo(date: string) {
  const diff = Date.now() - new Date(date).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  return `${Math.floor(hours / 24)}d ago`;
}

export default function Customers() {
  const { customers, deleteCustomer } = useCustomers();
  const { user } = useAuth();
  const canDelete = hasPermission(user?.role, "customers:delete");
  const navigate = useNavigate();
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("all");

  const filtered = useMemo(() => {
    let list = [...customers];
    if (statusFilter !== "all") list = list.filter((c) => c.status === statusFilter);
    if (search) {
      const q = search.toLowerCase();
      list = list.filter((c) => c.name.toLowerCase().includes(q) || c.phone.includes(q) || c.email.toLowerCase().includes(q));
    }
    return list.sort((a, b) => new Date(b.lastContact).getTime() - new Date(a.lastContact).getTime());
  }, [customers, search, statusFilter]);

  const handleDelete = (e: React.MouseEvent, c: Customer) => {
    e.stopPropagation();
    if (confirm(`Delete ${c.name}? This cannot be undone.`)) deleteCustomer(c.id);
  };

  const hotLeads = customers.filter((c) => c.status === "hot_lead").length;
  const activeCount = customers.filter((c) => c.status === "active").length;
  const newCount = customers.filter((c) => c.status === "new").length;
  const closedCount = customers.filter((c) => c.status === "closed").length;

  const summaryCards = [
    { label: "Total Customers", value: customers.length, icon: Users, gradient: "linear-gradient(135deg, #1B6EF3, #3B82F6)", shadow: "rgba(27, 110, 243, 0.3)" },
    { label: "Hot Leads", value: hotLeads, icon: Flame, gradient: "linear-gradient(135deg, #F97316, #FB923C)", shadow: "rgba(249, 115, 22, 0.3)" },
    { label: "Active", value: activeCount, icon: TrendingUp, gradient: "linear-gradient(135deg, #10B981, #34D399)", shadow: "rgba(16, 185, 129, 0.3)" },
    { label: "Closed Deals", value: closedCount, icon: CheckCircle, gradient: "linear-gradient(135deg, #8B5CF6, #A78BFA)", shadow: "rgba(139, 92, 246, 0.3)" },
  ];

  return (
    <div className="page">
      <div className="customers-banner">
        <div className="customers-banner-content">
          <div className="customers-banner-text">
            <h1>Customer CRM</h1>
            <p>{customers.length} customers &middot; {hotLeads} hot leads &middot; {newCount} new this week</p>
          </div>
          <button className="btn btn-add-customer" onClick={() => navigate("/customers/new")}>
            <Plus size={16} /> Add Customer
          </button>
        </div>
      </div>

      <div className="customer-stats-grid">
        {summaryCards.map((s) => (
          <div key={s.label} className="customer-stat-card" style={{ background: s.gradient, boxShadow: `0 4px 14px ${s.shadow}` }}>
            <div className="customer-stat-icon">
              <s.icon size={20} />
            </div>
            <div className="customer-stat-value">{s.value}</div>
            <div className="customer-stat-label">{s.label}</div>
          </div>
        ))}
      </div>

      <div className="card customers-table-card">
        <div className="filters-row">
          <div className="search-box">
            <Search size={16} />
            <input type="text" placeholder="Search by name, phone, or email..." value={search} onChange={(e) => setSearch(e.target.value)} />
          </div>
          <div className="status-filters">
            {STATUSES.map((s) => {
              const conf = STATUS_CONFIG[s];
              const isActive = statusFilter === s;
              return (
                <button key={s}
                  className={`filter-chip ${isActive ? "active" : ""}`}
                  style={isActive && s !== "all" ? { background: conf?.gradient, borderColor: conf?.color, color: "#fff" } : {}}
                  onClick={() => setStatusFilter(s)}>
                  {s === "all" ? "All" : conf?.label || s}
                </button>
              );
            })}
          </div>
        </div>

        {filtered.length === 0 ? (
          <div className="empty-state">
            <UserPlus size={40} />
            <h3>No customers found</h3>
            <p>{search ? "Try a different search term" : "Add your first customer to get started"}</p>
            {!search && (
              <button className="btn btn-primary" style={{ marginTop: 12 }} onClick={() => navigate("/customers/new")}>
                <Plus size={16} /> Add Customer
              </button>
            )}
          </div>
        ) : (
          <div className="customer-cards-list">
            {filtered.map((c) => {
              const conf = STATUS_CONFIG[c.status] || STATUS_CONFIG.new;
              return (
                <div key={c.id} className="customer-row-card" onClick={() => navigate(`/customers/${c.id}`)}>
                  <div className="customer-row-left">
                    <div className="customer-avatar" style={{ background: conf.bg, color: conf.color }}>
                      {c.name.split(" ").map((n) => n[0]).join("")}
                    </div>
                    <div className="customer-row-info">
                      <div className="customer-row-name">{c.name}</div>
                      <div className="customer-row-meta">
                        <span className="customer-row-phone">{c.phone}</span>
                        {c.needs && <span className="customer-row-needs">{c.needs}</span>}
                      </div>
                    </div>
                  </div>
                  <div className="customer-row-right">
                    <span className="customer-status-pill" style={{ background: conf.bg, color: conf.color, borderColor: conf.color }}>
                      {conf.label}
                    </span>
                    {c.budget && <span className="customer-row-budget">{c.budget}</span>}
                    <span className="customer-row-time">{timeAgo(c.lastContact)}</span>
                    <div className="customer-row-actions">
                      <a href={`tel:${c.phone}`} className="customer-action-icon" onClick={(e) => e.stopPropagation()} title="Call" style={{ background: "#E8F0FE", color: "#1B6EF3" }}>
                        <Phone size={14} />
                      </a>
                      <a href={`mailto:${c.email}`} className="customer-action-icon" onClick={(e) => e.stopPropagation()} title="Email" style={{ background: "#F5F3FF", color: "#8B5CF6" }}>
                        <Mail size={14} />
                      </a>
                      {canDelete && (
                        <button className="customer-action-icon danger" onClick={(e) => handleDelete(e, c)} title="Delete">
                          <Trash2 size={14} />
                        </button>
                      )}
                    </div>
                    <ArrowRight size={16} className="customer-row-arrow" />
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
